import { PropsWithChildren, Suspense } from 'react';
import styled from '@emotion/styled';
import { Header, Footer } from '@myrealtrip/layout-ui';
import { media } from '@myrealtrip/modash';

import { TARGET_ENV } from '~config/publicEnv';
import { useMember } from '~queries/member/useMember';
import { useMounted } from '~hooks/useMounted';
import { DESKTOP_PRIMARY_CONTAINER_WIDTH, MRT_FOOTER_ID } from '~constants/common';

import ErrorBoundary from './ErrorBoundary';
import dynamic from 'next/dynamic';
import { useWebView } from '@myrealtrip/device-detect';

const LiveCommerceFloatingButton = dynamic(() => import('remote/LiveCommerce'), {
  ssr: false,
  loading: () => null,
});

interface Props {
  className?: string;
  hideHeader?: boolean;
  hideFooter?: boolean;
  isFullWidth?: boolean;
  maxWidth?: number;
}

export default function CommonLayout({
  className,
  hideHeader = false,
  hideFooter = true,
  isFullWidth = false,
  maxWidth = DESKTOP_PRIMARY_CONTAINER_WIDTH,
  children,
}: PropsWithChildren<Props>) {
  const isInitWidget = useMounted();
  const { isWebView } = useWebView();

  const { data } = useMember();
  const { id, name, emailAddress, activeCouponCodeCount, point, phoneNumber, profileImageUrl } =
    data ?? {};
  const user = {
    id: id ?? 0,
    hashMemberId: '',
    username: name ?? '',
    email: emailAddress ?? '',
    phoneNumber: phoneNumber ?? '',
    point: point ?? 0,
    numberOfCoupons: activeCouponCodeCount ?? 0,
    profileImage: profileImageUrl ?? '',
  };

  return (
    <>
      <FrontHeaderStyleCover isHide={hideHeader}>
        <Header
          theme="white"
          size="default"
          user={user}
          targetEnv={TARGET_ENV}
          isDisabled={false}
          // PST-570 — 교통(유럽 기차)은 해외 전용 → region 해외 고정.
          // 주입 region 이 최우선(injected ?? URL ?? 쿠키 ?? overseas)이라 ?isDomestic= 로도 뒤집히지 않는다.
          region="overseas"
        />
      </FrontHeaderStyleCover>
      <Layout isFullWidth={isFullWidth} maxWidth={maxWidth} className={className}>
        <>{children}</>
      </Layout>
      <FrontFooterStyleCover isHide={hideFooter} id={MRT_FOOTER_ID}>
        <Footer targetEnv={TARGET_ENV} />
      </FrontFooterStyleCover>
      {isInitWidget && !isWebView && (
        <ErrorBoundary fallback={null} name="livecommerce">
          <Suspense>
            <LiveCommerceFloatingButton />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
}

const FrontHeaderStyleCover = styled.div<{
  isHide?: boolean;
}>`
  display: ${({ isHide }) => (isHide ? 'none' : 'block')};
`;

const FrontFooterStyleCover = styled.div<{
  isHide?: boolean;
}>`
  display: ${({ isHide }) => (isHide ? 'none' : 'block')};
`;

const Layout = styled.main<{ isFullWidth: boolean; maxWidth: number }>`
  flex: 1;
  width: 100%;
  max-width: ${({ isFullWidth, maxWidth }) => (isFullWidth ? '100%' : `${maxWidth}px`)};
  margin: 0 auto;
  background-color: ${({ theme }) => theme.colors.white};

  ${media.mobile} {
    width: 100%;
    margin: 0;
  }
`;
